import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button, useIsMobile } from '../ui';

const DISMISS_KEY = 'bb_location_prompt_dismissed';

/*
 * Prompts donors whose stored location has no coordinates.
 *
 * Matching measures distance from a request to each donor, so a donor with only
 * a typed city name — every donor who set their location before the country/
 * state/city picker existed, plus anyone whose old city name didn't match a
 * known one during the backfill — is skipped by every nearby-request alert.
 * They look set up and hear nothing, so the fix has to be surfaced rather than
 * waited on.
 */
export default function LocationBanner() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  // Only donors: a recipient without coordinates is inconvenienced, a donor
  // without them is silently unreachable.
  if (!user || user.role !== 'donor' || dismissed) return null;
  if (user.latitude != null && user.longitude != null) return null;

  const dismiss = () => {
    setDismissed(true);
    // Session-scoped on purpose — dismissing shouldn't permanently hide the
    // fact that they aren't receiving alerts.
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
  };

  return (
    <div style={{
      background: 'var(--amber-light)', borderBottom: '1px solid #FDD07A',
      padding: isMobile ? '12px 16px' : '10px 24px',
    }}>
      <div style={{
        maxWidth: '1200px', margin: '0 auto',
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '10px' : '20px', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          <MapPin size={16} color="var(--amber-dark)" />
          <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--amber-dark)' }}>
            Confirm your location so we can alert you about requests near you
          </span>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Link to="/account/settings">
            <Button size="sm" variant="secondary">Set my location</Button>
          </Link>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', color: 'var(--amber-dark)' }}
          >
            <X size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
